import React, { useCallback, useEffect, useState } from 'react';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

type BackupStatus = {
  last_export_at: string | null;
  last_import_at: string | null;
  estimated_archive_size_mb: number;
};

type Validation = {
  valid: boolean;
  version: string;
  warnings: string[];
  errors: string[];
  summary: {
    codex_chunks: number;
    projects: number;
    scenes: number;
    voice_profiles: number;
    decision_events: number;
    skills: number;
  };
};

type ImportResult = {
  success: boolean;
  imported: Validation['summary'];
  skipped: Validation['summary'];
  errors: string[];
};

export const BackupPanel: React.FC = () => {
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<Validation | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API_V1}/backup/status`, { headers: buildEngineHeaders() });
      if (!resp.ok) return;
      setStatus((await resp.json()) as BackupStatus);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const onExport = async () => {
    setDownloading(true);
    setMessage(null);
    try {
      const resp = await fetch(`${API_V1}/backup/export`, {
        method: 'POST',
        headers: buildEngineHeaders(),
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sakme-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('Export complete.');
      await loadStatus();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const onValidate = async () => {
    if (!file) return;
    setValidating(true);
    setValidation(null);
    setResult(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const resp = await fetch(`${API_V1}/backup/validate`, {
        method: 'POST',
        headers: buildEngineHeaders({ body: data }),
        body: data,
      });
      if (!resp.ok) throw new Error('Validation failed');
      setValidation((await resp.json()) as Validation);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const onImport = async () => {
    if (!file || !validation?.valid) return;
    setImporting(true);
    setResult(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const resp = await fetch(`${API_V1}/backup/import?merge_strategy=skip_duplicates`, {
        method: 'POST',
        headers: buildEngineHeaders({ body: data }),
        body: data,
      });
      if (!resp.ok) throw new Error('Import failed');
      setResult((await resp.json()) as ImportResult);
      await loadStatus();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-3 rounded border border-gray-800 p-3">
      <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">Backup & Restore</h4>

      <div className="flex items-center justify-between">
        <div className="text-[10px] text-gray-500">
          Estimated archive size: {status?.estimated_archive_size_mb ?? 0} MB
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={downloading}
          className="rounded border border-gray-700 px-3 py-1.5 text-xs font-mono text-gray-300 disabled:opacity-50"
        >
          {downloading ? 'Exporting...' : 'Download Your Memory'}
        </button>
      </div>

      <div className="space-y-2">
        <input
          type="file"
          accept=".json,application/json"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-xs text-gray-400"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onValidate}
            disabled={!file || validating}
            className="rounded border border-gray-700 px-3 py-1.5 text-xs font-mono text-gray-300 disabled:opacity-50"
          >
            {validating ? 'Validating...' : 'Validate Import'}
          </button>
          <button
            type="button"
            onClick={onImport}
            disabled={!validation?.valid || importing}
            className="rounded border border-gray-700 px-3 py-1.5 text-xs font-mono text-gray-300 disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>

      {validation ? (
        <div className="text-[10px] text-gray-400">
          <div>Archive version: {validation.version}</div>
          <div>
            Summary: {validation.summary.codex_chunks} chunks, {validation.summary.projects} projects,{' '}
            {validation.summary.scenes} scenes
          </div>
          {validation.warnings.length ? <div>Warnings: {validation.warnings.join(' | ')}</div> : null}
          {validation.errors.length ? <div className="text-red-400">Errors: {validation.errors.join(' | ')}</div> : null}
        </div>
      ) : null}

      {result ? (
        <div className="text-[10px] text-gray-400">
          <div>Import: {result.success ? 'success' : 'failed'}</div>
          <div>
            Imported: {result.imported.codex_chunks} chunks, {result.imported.projects} projects,{' '}
            {result.imported.scenes} scenes
          </div>
          <div>
            Skipped: {result.skipped.codex_chunks} chunks, {result.skipped.projects} projects,{' '}
            {result.skipped.scenes} scenes
          </div>
          {result.errors.length ? <div className="text-red-400">Errors: {result.errors.join(' | ')}</div> : null}
        </div>
      ) : null}

      <div className="text-[10px] text-gray-500">
        Last export: {status?.last_export_at ?? 'never'} | Last import: {status?.last_import_at ?? 'never'}
      </div>
      {message ? <div className="text-[10px] text-amber-400">{message}</div> : null}
    </div>
  );
};
